import { ActivityBehavior } from './ActivityBehavior';
import lessonManager from '../managers/LessonManager';
import responseManager from '../managers/ResponseManager';
import responseService from '../services/ResponseService';
import Auth from '../services/AuthService';
import studentActivityManager from '../managers/StudentActivityManager';

const getResponseRecordId = (resp) => resp?.id || resp?.activityResponseId || resp?.responseId || null;

export class MicrositeQuestionBehavior extends ActivityBehavior {
  hasResponseDependency = (model, lessonElementId) => {
    const responseDependency = lessonManager.lessonOptions?.responseDependency;
    const responseDependencyValue = responseDependency && responseDependency[lessonElementId];

    if (!responseDependencyValue) {
      return false;
    }

    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        return responseDependencyValue.questionId;
      }
      if (responseDependencyValue.source === 'differentLesson') {
        return responseDependencyValue.lessonId &&
          responseDependencyValue.questionForeverId &&
          responseDependencyValue.lessonId !== '' &&
          responseDependencyValue.questionForeverId !== '';
      }
    }

    return responseDependencyValue !== null && responseDependencyValue !== '';
  }

  resolvePreviousQuestionLessonElementId = (previousQuestionId) => {
    if (!previousQuestionId) {
      return null;
    }

    const directState = responseManager.getLessonElementState(previousQuestionId);
    if (directState?.responseId) {
      return previousQuestionId;
    }

    const matchedModel = lessonManager.elementsThatHaveState.find((elementModel) => (
      elementModel.lessonElementId === previousQuestionId || elementModel.id === previousQuestionId
    ));

    return matchedModel?.lessonElementId || previousQuestionId;
  }

  buildPullForwardSourceContext = ({ sourceActivityResponseId, sourceActivityId } = {}) => {
    if (!sourceActivityResponseId) {
      return null;
    }

    return {
      sourceActivityResponseId,
      sourceActivityId: sourceActivityId || studentActivityManager.activityId || null,
    };
  }

  getInLessonPullForwardSourceContext = (model, lessonElementId) => {
    if (!this.hasResponseDependency(model, lessonElementId)) {
      return null;
    }

    const responseDependency = lessonManager.lessonOptions?.responseDependency;
    const responseDependencyValue = responseDependency[lessonElementId];
    let previousQuestionId;

    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        previousQuestionId = responseDependencyValue.questionId;
      } else {
        return null;
      }
    } else {
      previousQuestionId = responseDependencyValue;
    }

    const resolvedPreviousQuestionId = this.resolvePreviousQuestionLessonElementId(previousQuestionId);
    const previousQuestionState = responseManager.getLessonElementState(resolvedPreviousQuestionId);

    return this.buildPullForwardSourceContext({
      sourceActivityResponseId: previousQuestionState?.responseId,
      sourceActivityId: studentActivityManager.activityId,
    });
  }

  fetchPullForwardSourceContextFromDifferentLesson = async (responseDependencyValue) => {
    try {
      const { lessonId, questionForeverId } = responseDependencyValue;
      const activityId = studentActivityManager.studentActivityId;

      if (!activityId) {
        console.warn('No activity ID available for fetching prior responses');
        return null;
      }

      const response = await Auth.fetch(`${Auth.ecms}/api/getPriorResponses?lessonForeverId=${lessonId}&questionForeverId=${questionForeverId}&activityId=${activityId}`, {
        method: 'GET',
      });

      if (response && response.responses && Array.isArray(response.responses)) {
        const responses = response.responses.filter((resp) => getResponseRecordId(resp));

        if (responses.length === 0) {
          responseService.alertEmptyPreviousResponse();
          return null;
        }

        let selectedResponse = responses[0];
        if (responses.length > 1) {
          selectedResponse = await this.showResponseSelectionDialog(responses);
        }

        if (!selectedResponse) {
          return null;
        }

        return this.buildPullForwardSourceContext({
          sourceActivityResponseId: getResponseRecordId(selectedResponse),
          sourceActivityId: selectedResponse.activityId || activityId,
        });
      }

      responseService.alertEmptyPreviousResponse();
      return null;
    } catch (error) {
      console.error('Error fetching prior responses from different lesson:', error);
      responseService.alertEmptyPreviousResponse();
      return null;
    }
  }

  showResponseSelectionDialog = async (responses) => {
    try {
      const { showResponseSelectionDialog } = await import('../components/dialogs/ResponseSelectionDialog');

      const responseData = responses.map((resp, index) => ({
        id: getResponseRecordId(resp),
        index: index + 1,
        creationDate: resp.creationTimestamp,
        modificationDate: resp.modificationTimestamp,
        activityName: resp.activityName,
      })).filter((resp) => resp.id);

      const selectedResponse = await showResponseSelectionDialog({
        responses: responseData,
        type: 'microsite',
      });

      if (!selectedResponse?.id) {
        return null;
      }

      return responses.find((resp) => getResponseRecordId(resp) === selectedResponse.id) || selectedResponse;
    } catch (error) {
      console.error('Error showing response selection dialog:', error);
      return null;
    }
  }
}

export default new MicrositeQuestionBehavior();
